import React from 'react';
import { Box, Chip, Paper, Typography } from '@mui/material';
import { brandColors } from '@config/brandTokens';
import { getSportsIqQuestions, type SportsIqDifficulty } from '@/content/sportsIqPlaceholder';

const FONT = '"Sora", Arial, sans-serif';

const difficultySx = (difficulty: SportsIqDifficulty) => {
  if (difficulty === 'Easy') {
    return { bgcolor: '#ecfdf5', color: brandColors.green, borderColor: '#bbf7d0' };
  }
  if (difficulty === 'Hard') {
    return { bgcolor: '#fff7ed', color: brandColors.orange, borderColor: '#fdba74' };
  }
  return { bgcolor: '#eef2ff', color: brandColors.blue, borderColor: '#c7d2fe' };
};

export interface ContestSportsIqTabPanelProps {
  sportName: string;
}

/** Planned Sports IQ tab — tactics/decision questions, not technical skill scoring. */
export const ContestSportsIqTabPanel: React.FC<ContestSportsIqTabPanelProps> = ({ sportName }) => {
  const questions = getSportsIqQuestions(sportName);

  return (
    <Paper
      elevation={0}
      sx={{
        width: '100%',
        borderRadius: 2.5,
        border: '1px solid #e2e8f0',
        overflow: 'hidden',
        bgcolor: brandColors.white,
        boxShadow: '0 1px 4px rgba(17,23,57,0.06)',
      }}
    >
      <Box
        sx={{
          px: { xs: 1.5, md: 2 },
          py: 1.5,
          display: 'flex',
          alignItems: { xs: 'flex-start', sm: 'center' },
          justifyContent: 'space-between',
          gap: 1,
          flexWrap: 'wrap',
          borderBottom: '2px solid #CA8A04',
          bgcolor: brandColors.white,
        }}
      >
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Typography
            sx={{
              fontWeight: 800,
              fontSize: { xs: '0.95rem', md: '1.05rem' },
              color: brandColors.navy,
              fontFamily: FONT,
              lineHeight: 1.3,
            }}
          >
            Sports IQ — {sportName} tactics & decision-making
          </Typography>
          <Typography
            sx={{
              mt: 0.5,
              fontSize: '0.82rem',
              fontWeight: 500,
              color: brandColors.slate,
              fontFamily: FONT,
              lineHeight: 1.45,
              maxWidth: 720,
            }}
          >
            Test how you read the game: shot selection, positioning, and match-ups. This is not a
            technical-skill score, self-evaluation, or contest video grade — those stay on their own
            tabs.
          </Typography>
        </Box>
        <Chip
          size="small"
          label="Coming soon"
          sx={{
            fontWeight: 800,
            fontFamily: FONT,
            bgcolor: '#fef9c3',
            color: '#a16207',
            border: '1px solid #fde047',
          }}
        />
      </Box>

      <Typography
        sx={{
          px: { xs: 1.5, md: 2 },
          py: 1,
          fontSize: '0.875rem',
          fontWeight: 400,
          color: brandColors.slate,
          fontFamily: FONT,
          bgcolor: brandColors.ice,
          borderBottom: '1px solid #e2e8f0',
        }}
      >
        {questions.length} sample questions for {sportName}. Preview only — answers are not scored
        and the quiz engine ships later.
      </Typography>

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25, p: { xs: 1.5, md: 2 } }}>
        {questions.map((question, index) => (
          <Box
            key={question.id}
            sx={{
              border: '1px solid #e2e8f0',
              borderRadius: 2,
              borderLeft: '4px solid #CA8A04',
              bgcolor: brandColors.white,
              px: 1.5,
              py: 1.25,
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap', mb: 0.75 }}>
              <Typography
                sx={{
                  fontWeight: 800,
                  fontSize: '0.72rem',
                  letterSpacing: 0.6,
                  textTransform: 'uppercase',
                  color: brandColors.navy,
                  fontFamily: FONT,
                }}
              >
                Q{index + 1}
              </Typography>
              <Chip
                size="small"
                label={question.type}
                sx={{
                  height: 22,
                  fontWeight: 700,
                  fontSize: '0.68rem',
                  fontFamily: FONT,
                  bgcolor: brandColors.navy,
                  color: brandColors.white,
                }}
              />
              <Chip
                size="small"
                label={question.difficulty}
                sx={{
                  height: 22,
                  fontWeight: 700,
                  fontSize: '0.68rem',
                  fontFamily: FONT,
                  border: '1px solid',
                  ...difficultySx(question.difficulty),
                }}
              />
            </Box>
            <Typography
              sx={{
                fontWeight: 700,
                fontSize: '0.9rem',
                color: brandColors.navy,
                fontFamily: FONT,
                lineHeight: 1.4,
                mb: 1,
              }}
            >
              {question.prompt}
            </Typography>
            <Box component="ul" sx={{ m: 0, pl: 0, listStyle: 'none', display: 'flex', flexDirection: 'column', gap: 0.5 }}>
              {question.options.map((option) => (
                <Box
                  key={option}
                  component="li"
                  sx={{
                    px: 1.25,
                    py: 0.7,
                    borderRadius: 1,
                    border: '1px solid #e2e8f0',
                    bgcolor: brandColors.ice,
                    color: brandColors.slate,
                    fontSize: '0.82rem',
                    fontFamily: FONT,
                    fontWeight: 500,
                    lineHeight: 1.4,
                  }}
                >
                  {option}
                </Box>
              ))}
            </Box>
          </Box>
        ))}
      </Box>
    </Paper>
  );
};

export default ContestSportsIqTabPanel;
